import { useState, useEffect, useCallback } from 'react';
import { Check, Plus, Star, Car as SteeringWheel, X, HardHat } from 'lucide-react';
import { api } from '../../lib/api';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { OrgUser } from '../../types/admin';
import type { AssignedCrewMember } from '../../hooks/useJobs';

interface CrewAssignmentModalProps {
  orgId: string;
  jobId: string;
  moveDate: string | undefined;
  crewNeeded: number;
  driverQuantity: number;
  quoteType: 'commercial' | 'residential';
  currentCrew: AssignedCrewMember[];
  onSave: (crew: AssignedCrewMember[]) => Promise<void>;
  onClose: () => void;
}

export function CrewAssignmentModal({
  orgId,
  jobId,
  moveDate,
  crewNeeded,
  driverQuantity,
  quoteType,
  currentCrew,
  onSave,
  onClose,
}: CrewAssignmentModalProps) {
  const [crew, setCrew] = useState<AssignedCrewMember[]>(currentCrew);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [busyUserIds, setBusyUserIds] = useState<Set<string>>(new Set());
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contractorName, setContractorName] = useState('');
  const [showContractorInput, setShowContractorInput] = useState(false);

  const crewCount = crew.length;
  const driverCount = crew.filter(c => c.isDriver).length;
  const driversNeeded = quoteType === 'residential' ? (crewNeeded > 0 ? driverQuantity : 0) : driverQuantity;
  const allSlotsFilled = crewCount >= crewNeeded && crewNeeded > 0;

  // Fetch org users + availability
  useEffect(() => {
    if (!orgId) return;
    setLoadingUsers(true);

    const usersPromise = api.get<{ success: boolean; data: OrgUser[] }>(`/org-admin/${orgId}/users`);
    const availPromise = moveDate
      ? api.get<{ success: boolean; data: { busyCrewUserIds: string[]; busyTruckIds: string[] } }>(
          `/jobs/${orgId}/availability?date=${moveDate}&excludeJobId=${jobId}`,
        )
      : Promise.resolve({ data: { busyCrewUserIds: [] as string[], busyTruckIds: [] as string[] } });

    Promise.all([usersPromise, availPromise])
      .then(([usersRes, availRes]) => {
        setOrgUsers(usersRes.data);
        setBusyUserIds(new Set(availRes.data.busyCrewUserIds));
      })
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [orgId, moveDate, jobId]);

  // Auto-close when all slots filled
  useEffect(() => {
    if (allSlotsFilled && crewCount > currentCrew.length) {
      handleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSlotsFilled, crewCount]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(crew);
      onClose();
    } catch {
      setSaving(false);
    }
  }, [crew, onSave, onClose]);

  function assignUser(user: OrgUser) {
    if (crew.some(c => c.userId === user.uid)) return;
    if (crewCount >= crewNeeded && crewNeeded > 0) return;
    setCrew(prev => [...prev, {
      userId: user.uid,
      displayName: user.displayName || user.email,
      isCrewLead: prev.length === 0, // First person becomes crew lead by default
      isDriver: false,
      isContractor: false,
    }]);
  }

  function addContractor() {
    const name = contractorName.trim();
    if (!name) return;
    if (crewCount >= crewNeeded && crewNeeded > 0) return;
    setCrew(prev => [...prev, {
      displayName: name,
      isCrewLead: prev.length === 0,
      isDriver: false,
      isContractor: true,
    }]);
    setContractorName('');
    setShowContractorInput(false);
  }

  function removeMember(index: number) {
    setCrew(prev => {
      const next = prev.filter((_, i) => i !== index);
      // If removed member was crew lead, assign lead to first remaining member
      if (prev[index].isCrewLead && next.length > 0) {
        next[0] = { ...next[0], isCrewLead: true };
      }
      return next;
    });
  }

  function toggleCrewLead(index: number) {
    setCrew(prev => prev.map((c, i) => ({
      ...c,
      isCrewLead: i === index,
    })));
  }

  function toggleDriver(index: number) {
    setCrew(prev => prev.map((c, i) => i === index ? { ...c, isDriver: !c.isDriver } : c));
  }

  // Available users: active movers + admins not already assigned and not busy
  const assignedUserIds = new Set(crew.filter(c => c.userId).map(c => c.userId!));
  const availableUsers = orgUsers.filter(u =>
    u.status === 'active' &&
    (u.role === 'mover' || u.role === 'admin' || u.role === 'field_lead') &&
    !assignedUserIds.has(u.uid) &&
    !busyUserIds.has(u.uid),
  );
  const busyUsers = orgUsers.filter(u =>
    u.status === 'active' &&
    (u.role === 'mover' || u.role === 'admin' || u.role === 'field_lead') &&
    !assignedUserIds.has(u.uid) &&
    busyUserIds.has(u.uid),
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 md:rounded-2xl md:max-w-md md:w-full md:pb-6 max-h-[85vh] flex flex-col">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-h3 text-midnight">Assign Crew</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-[13px] text-slate">
                <span className="font-semibold text-midnight">{crewCount}</span> / {crewNeeded} crew
              </span>
              {driversNeeded > 0 && (
                <span className="text-[13px] text-slate">
                  <span className="font-semibold text-midnight">{driverCount}</span> / {driversNeeded} drivers
                </span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors shrink-0"
            aria-label="Close"><X size={18} /></button>
        </div>

        {/* Assigned crew list */}
        {crew.length > 0 && (
          <div className="mb-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Assigned</p>
            <ul className="flex flex-col gap-1.5">
              {crew.map((member, i) => (
                <li key={member.userId ?? `contractor-${i}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-plum/20 bg-plum/5">
                  <div className="w-7 h-7 rounded-full bg-plum/10 flex items-center justify-center shrink-0 text-[12px] font-semibold text-plum">
                    {member.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-midnight truncate flex items-center gap-1.5">
                      {member.displayName}
                      {member.isCrewLead && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-bold uppercase">
                          <Star size={9} /> Lead
                        </span>
                      )}
                      {member.isDriver && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[9px] font-bold uppercase">
                          <SteeringWheel size={9} /> Driver
                        </span>
                      )}
                      {member.isContractor && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-stone text-slate text-[9px] font-bold uppercase">
                          <HardHat size={9} /> Contractor
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => toggleCrewLead(i)}
                      title="Set as Crew Lead"
                      className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors
                        ${member.isCrewLead ? 'text-amber-600 bg-amber-100' : 'text-slate hover:text-amber-600 hover:bg-amber-50'}`}>
                      <Star size={13} />
                    </button>
                    <button type="button" onClick={() => toggleDriver(i)}
                      title="Toggle Driver"
                      className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors
                        ${member.isDriver ? 'text-blue-600 bg-blue-100' : 'text-slate hover:text-blue-600 hover:bg-blue-50'}`}>
                      <SteeringWheel size={13} />
                    </button>
                    <button type="button" onClick={() => removeMember(i)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-slate hover:text-rose hover:bg-rose/10 transition-colors"
                      aria-label="Remove">
                      <X size={13} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Available users */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 min-h-0">
          {loadingUsers ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : (
            <>
              {!allSlotsFilled && (
                <>
                  {availableUsers.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Available</p>
                      <ul className="flex flex-col gap-1">
                        {availableUsers.map(u => (
                          <li key={u.uid}>
                            <button type="button" onClick={() => assignUser(u)}
                              className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl border border-stone
                                         hover:border-plum/30 hover:bg-plum/5 active:bg-mist transition-all duration-150">
                              <div className="w-7 h-7 rounded-full bg-plum/10 flex items-center justify-center shrink-0 text-[12px] font-semibold text-plum">
                                {(u.displayName || u.email).charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-medium text-midnight truncate">{u.displayName || u.email}</p>
                                <p className="text-[11px] text-slate">{u.role === 'admin' ? 'Admin' : u.role === 'field_lead' ? 'Field Lead' : 'Mover'}</p>
                              </div>
                              <Plus size={14} className="text-plum shrink-0" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {busyUsers.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate/60 mb-2">Unavailable (assigned elsewhere)</p>
                      <ul className="flex flex-col gap-1">
                        {busyUsers.map(u => (
                          <li key={u.uid}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-stone/50 opacity-50">
                            <div className="w-7 h-7 rounded-full bg-stone/30 flex items-center justify-center shrink-0 text-[12px] font-semibold text-slate">
                              {(u.displayName || u.email).charAt(0).toUpperCase()}
                            </div>
                            <p className="text-[13px] text-slate truncate">{u.displayName || u.email}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Add Contractor */}
                  <div className="mb-3">
                    {showContractorInput ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          autoFocus
                          value={contractorName}
                          onChange={e => setContractorName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') addContractor(); if (e.key === 'Escape') setShowContractorInput(false); }}
                          placeholder="Contractor name"
                          className="flex-1 h-10 px-3 rounded-lg border border-stone bg-white text-[13px] text-midnight
                                     placeholder:text-slate focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20"
                        />
                        <button type="button" onClick={addContractor}
                          disabled={!contractorName.trim()}
                          className="h-10 px-3 rounded-lg bg-plum text-white text-[13px] font-medium
                                     hover:bg-plum/90 disabled:opacity-50 transition-colors">
                          Add
                        </button>
                        <button type="button" onClick={() => { setShowContractorInput(false); setContractorName(''); }}
                          className="w-10 h-10 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setShowContractorInput(true)}
                        className="w-full flex items-center justify-center gap-2 h-10 rounded-xl border border-dashed border-slate/40
                                   text-[13px] font-medium text-slate hover:text-midnight hover:border-slate/60 hover:bg-mist transition-colors">
                        <HardHat size={14} />
                        Add Contractor
                      </button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Save button (shown when changes were made but auto-close didn't trigger) */}
        {!allSlotsFilled && crew.length !== currentCrew.length && (
          <button type="button" onClick={handleSave} disabled={saving}
            className="mt-3 w-full h-11 rounded-xl bg-plum text-white text-[14px] font-semibold
                       hover:bg-plum/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
            {saving ? <LoadingSpinner size="sm" /> : <Check size={16} />}
            {saving ? 'Saving…' : 'Save Crew'}
          </button>
        )}
      </div>
    </div>
  );
}
